import React from 'react';
import { Calendar, Activity } from 'lucide-react';
import { apiClient } from '../../lib/apiClient';
import { REPORT_ACCENT } from './reportTheme';
import {
  bucketByView,
  pickDefaultView,
  type TrendView,
  type TrendDatum as DailyDatum,
  type TrendBucket as WeekBucket,
} from './trendBuckets';
import { niceAxisRange, fmtAxis } from './chartAxis';
import { rangeQuery } from './reportRange';
import RangeBadge from './RangeBadge';

// CTV-445: trend charts at the top of the report — plays by week (time trend)
// and plays by day-of-week. Both are derived client-side from the same
// /submissions/:id/delivery-daily response the delivery card uses, so there's no
// new endpoint. Modeled on the client-facing TBN performance report's
// "Performance By Week" / "Performance By Day of Week" charts.
// CTV-560: the day-of-week chart is plays-only (Completes was removed because
// the dual line made the chart hard to read at small scale; Daypart + Delivery
// charts still surface completes).

// The day view shows at most this many of the most recent delivered days.
const DAY_VIEW_WINDOW_DAYS = 30;

// Shift a YYYY-MM-DD date string by `delta` days (UTC, so no DST drift).
function addDays(dateStr: string, delta: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().slice(0, 10);
}

interface DowBucket {
  name: string;
  plays: number;
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function bucketByDayOfWeek(daily: DailyDatum[]): DowBucket[] {
  const plays = Array(7).fill(0) as number[];
  for (const d of daily) {
    const [y, m, day] = d.date.split('-').map(Number);
    const dow = new Date(y, m - 1, day).getDay(); // 0 = Sun
    plays[dow] += d.plays;
  }
  return DOW.map((name, i) => ({ name, plays: plays[i] }));
}

interface PerformanceTrendsProps {
  submissionId: string;
  accentColor?: string;
  // Brand secondary for small label accents (falls back to the primary).
  secondaryColor?: string;
  from?: string | null;
  to?: string | null;
  rangeLabel?: string | null;
  // Which chart(s) to render: 'both' (side-by-side, default), or a single chart
  // ('week' full-width, 'dow') so the page can place them independently.
  variant?: 'both' | 'week' | 'dow';
}

const PerformanceTrends: React.FC<PerformanceTrendsProps> = ({
  submissionId,
  accentColor,
  secondaryColor,
  from,
  to,
  rangeLabel,
  variant = 'both',
}) => {
  const accent = accentColor || REPORT_ACCENT;
  const marker = secondaryColor || accent;
  const [daily, setDaily] = React.useState<DailyDatum[] | null>(null);
  // Dim (not blank) the charts while a new range loads, so they don't collapse
  // and reflow on every filter press.
  const [refreshing, setRefreshing] = React.useState(false);
  // "View by" granularity for the time-trend chart. null = auto (pick the finest
  // that fits the bar cap); a user pick overrides until they change it.
  const [view, setView] = React.useState<TrendView | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setRefreshing(true);
    apiClient
      .get<{ daily: Array<{ date: string; plays: number; completes?: number }> }>(
        `/submissions/${submissionId}/delivery-daily${rangeQuery({ from: from ?? null, to: to ?? null })}`,
      )
      .then((d) => {
        if (cancelled) return;
        setDaily(
          (d.daily ?? []).map((r) => ({ date: r.date, plays: r.plays ?? 0, completes: r.completes ?? 0 })),
        );
      })
      .catch(() => {
        if (!cancelled) setDaily([]);
      })
      .finally(() => {
        if (!cancelled) setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId, from, to]);

  // Effective view = the user's pick, or the auto default for the loaded data.
  const effectiveView = view ?? pickDefaultView(daily);
  // The "day" view never shows daily bars older than the most recent 30 delivered
  // days: for a long campaign that's a wall of bars and the day-level detail isn't
  // useful that far back. The window ends on the last delivered day (not today),
  // so a campaign that finished weeks ago still shows its final 30 days.
  const dayView = React.useMemo(() => {
    if (!daily || daily.length === 0) return { data: daily ?? [], clipped: false };
    const last = daily.reduce((m, d) => (d.date > m ? d.date : m), daily[0].date);
    const start = addDays(last, -(DAY_VIEW_WINDOW_DAYS - 1));
    const data = daily.filter((d) => d.date >= start);
    return { data, clipped: data.length < daily.length };
  }, [daily]);
  const buckets = React.useMemo(() => {
    if (!daily) return [];
    const src = effectiveView === 'day' ? dayView.data : daily;
    // When the source spans more than one calendar year, labels need the year
    // ("May 17, 2025") so they aren't ambiguous across years.
    const multiYear = new Set(src.map((d) => d.date.slice(0, 4))).size > 1;
    return bucketByView(src, effectiveView, multiYear);
  }, [daily, effectiveView, dayView]);
  const dow = React.useMemo(() => (daily ? bucketByDayOfWeek(daily) : []), [daily]);
  const totalPlays = React.useMemo(() => (daily ?? []).reduce((a, d) => a + d.plays, 0), [daily]);

  // Self-hide while loading and when there's no delivery — this sits directly
  // under the insights band (which self-hides the same way), so the report top
  // stays clean rather than flashing empty chart frames.
  if (daily === null || totalPlays === 0) return null;

  const dimCls = `transition-opacity duration-300 ${refreshing ? 'opacity-60' : 'opacity-100'}`;
  const weekly = (
    <WeeklyTrend buckets={buckets} view={effectiveView} onViewChange={setView} accent={accent} marker={marker} rangeLabel={rangeLabel} clippedToRecent={effectiveView === 'day' && dayView.clipped} />
  );
  const dayOfWeek = <DayOfWeekTrend dow={dow} accent={accent} marker={marker} rangeLabel={rangeLabel} />;

  // Single chart spanning its container (e.g. full-width "plays by week", or the
  // day-of-week chart placed alongside the daypart card).
  if (variant === 'week') return <div className={dimCls}>{weekly}</div>;
  if (variant === 'dow') return <div className={`h-full ${dimCls}`}>{dayOfWeek}</div>;

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-5 items-start ${dimCls}`}>
      {weekly}
      {dayOfWeek}
    </div>
  );
};

// CTV-560: Plays-only legend — Completes was dropped from both this file's
// charts during CTV-560 (weekly bar chart never showed it; day-of-week chart
// stopped showing it). Daypart + Delivery cards still surface Completes in
// their own components.
const Legend: React.FC<{ accent: string }> = ({ accent }) => (
  <div className="flex items-center gap-3 text-[10px] text-slate-400 shrink-0">
    <span className="inline-flex items-center gap-1">
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: accent }} /> Plays
    </span>
  </div>
);

const WeeklyTrend: React.FC<{
  buckets: WeekBucket[];
  view: TrendView;
  onViewChange: (v: TrendView) => void;
  accent: string;
  marker: string;
  rangeLabel?: string | null;
  // True when the day view is clipped to the most recent 30 days (older days hidden).
  clippedToRecent?: boolean;
}> = ({ buckets, view, onViewChange, accent, marker, rangeLabel, clippedToRecent }) => {
  const [hover, setHover] = React.useState<number | null>(null);
  const noun = view === 'day' ? 'day' : view === 'month' ? 'month' : 'week';
  const adverbial = view === 'day' ? 'Daily' : view === 'month' ? 'Monthly' : 'Weekly';
  // Dynamic y-range floored near the data (not 0) so high-but-steady volume still
  // shows period-to-period variation. Bars are drawn from the floor.
  const vals = buckets.map((w) => w.plays);
  const { min, max, ticks } = niceAxisRange(Math.min(...vals), Math.max(1, ...vals));
  const yPct = (t: number) => (max > min ? (1 - (t - min) / (max - min)) * 100 : 50);
  const barH = (v: number) => (max > min ? Math.max(2, ((v - min) / (max - min)) * 100) : 2);
  const hovered = hover != null ? buckets[hover] : null;
  // Many buckets (e.g. ~52 weeks / ~365 days): drop the bars' min-width cap and
  // tighten the gaps so they all fit; per-bucket labels are also dropped since
  // they're illegible at that density (first/last only).
  const dense = buckets.length > 16;
  // Cap each bar's width, scaled to the bucket count. In the tall plot a handful
  // of full-stretch bars are thin slivers, so widen them as the count drops;
  // dense charts stay uncapped so ~52 bars still fit.
  const barMaxW = dense ? null : buckets.length <= 3 ? 64 : buckets.length <= 6 ? 48 : 34;
  // Cap (and center) the whole bars/labels band so on a wide card the bars
  // cluster in the middle instead of stranding edge-to-edge with big gaps (e.g.
  // 12 monthly bars). Each bucket's slot must fit the WIDER of the bar (bar +
  // gap) and its x-axis label — otherwise month/week labels carrying a year
  // ("Jul 2025") get truncated to "Jul 2…". ~6.5px per char at the 10px label
  // font. flex-1 inside still shrinks to fit narrow cards, so it never overflows;
  // dense charts use the full width (they label first/last only).
  const slot =
    barMaxW == null
      ? null
      : Math.max(barMaxW + 12, buckets.reduce((m, b) => Math.max(m, b.label.length), 0) * 6.5 + 10);
  const bandStyle = slot == null ? undefined : { maxWidth: `${buckets.length * slot}px` };

  return (
    <div>
      <div className="flex items-center justify-between mb-2 px-1 gap-3">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: accent }}>
          <span className="w-0.5 h-3 rounded-full" style={{ backgroundColor: marker }} />
          Plays by {noun}
          <RangeBadge label={rangeLabel} accentColor={accent} />
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden">
        <div className="px-5 pt-4 pb-2 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-2 text-[12px] text-slate-600 min-w-0">
            <Calendar className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} />
            {hovered ? (
              <span className="truncate">
                <span className="font-semibold text-slate-900">{hovered.plays.toLocaleString('en-US')}</span> plays
                <span className="text-slate-400"> · {view === 'week' ? `week of ${hovered.label}` : hovered.label}</span>
              </span>
            ) : clippedToRecent ? (
              <span>
                Daily plays <span className="text-slate-400">· most recent 30 days</span>
              </span>
            ) : (
              `${adverbial} plays`
            )}
          </span>
          <div className="flex items-center gap-3 shrink-0">
            {/* View-by granularity toggle. */}
            <div className="inline-flex items-center gap-0.5 rounded-full bg-slate-100 p-0.5">
              {(['day', 'week', 'month'] as TrendView[]).map((v) => {
                const active = view === v;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => onViewChange(v)}
                    aria-pressed={active}
                    className="px-2.5 py-0.5 rounded-full text-[11px] font-medium capitalize transition-colors"
                    style={active ? { backgroundColor: accent, color: '#fff' } : { color: 'rgb(71, 85, 105)' }}
                  >
                    {v}
                  </button>
                );
              })}
            </div>
            <Legend accent={accent} />
          </div>
        </div>

        <div className="px-5 pb-3">
          <div className="flex gap-1.5">
            {/* y-axis */}
            <div className="relative h-56 w-9 shrink-0">
              {ticks.map((t) => (
                <span
                  key={t}
                  className="absolute right-0 -translate-y-1/2 pr-1 text-[9px] tabular-nums text-slate-400"
                  style={{ top: `${yPct(t)}%` }}
                >
                  {fmtAxis(t)}
                </span>
              ))}
            </div>
            {/* plot */}
            <div className="flex-1 min-w-0">
              <div className="relative h-56">
                {ticks.map((t) => (
                  <div
                    key={t}
                    className="absolute left-0 right-0 border-t"
                    style={{ top: `${yPct(t)}%`, borderColor: t === min ? '#e2e8f0' : '#f1f5f9' }}
                  />
                ))}
                <div
                  className={`absolute inset-y-0 flex items-end ${dense ? 'inset-x-0 gap-px' : 'left-1/2 -translate-x-1/2 w-full gap-3'}`}
                  style={bandStyle}
                  onMouseLeave={() => setHover(null)}
                >
                  {buckets.map((w, i) => {
                    const dim = hover != null && hover !== i;
                    const barCls = `flex-1 min-w-0 transition-opacity ${dense ? 'rounded-t-sm' : 'rounded-t-md'}`;
                    return (
                      <div
                        key={w.key}
                        data-week-bar
                        className="flex-1 min-w-0 h-full flex items-end justify-center cursor-pointer"
                        onMouseEnter={() => setHover(i)}
                      >
                        <div
                          className={barCls}
                          style={{
                            height: `${barH(w.plays)}%`,
                            maxWidth: barMaxW != null ? `${barMaxW}px` : undefined,
                            backgroundColor: accent,
                            opacity: dim ? 0.4 : 0.9,
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Keep a bottom label row in both modes so this card stays the
                  same height as the day-of-week chart. Dense shows just the
                  first/last week (per-week labels are illegible at ~52 weeks). */}
              {dense ? (
                <div className="flex justify-between mt-2 px-0.5 text-[10px] text-slate-400">
                  <span>{buckets[0]?.label}</span>
                  <span>{buckets[buckets.length - 1]?.label}</span>
                </div>
              ) : (
                <div data-bar-labels className="flex gap-3 mt-2 mx-auto w-full" style={bandStyle}>
                  {buckets.map((w) => (
                    <span key={w.key} className="flex-1 text-center text-[10px] text-slate-400 truncate">
                      {w.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DayOfWeekTrend: React.FC<{ dow: DowBucket[]; accent: string; marker: string; rangeLabel?: string | null }> = ({ dow, accent, marker, rangeLabel }) => {
  const [hover, setHover] = React.useState<number | null>(null);
  // Dynamic y-range floored near the data (not 0) so values clustered high use
  // the full plot height.
  const vals = dow.map((d) => d.plays);
  const { min, max, ticks } = niceAxisRange(Math.min(...vals), Math.max(1, ...vals));
  const hovered = hover != null ? dow[hover] : null;

  // Lay the 7 points out at flex-cell centers (matching the weekly bars) inside
  // a 0..100 viewBox; the SVG stretches to fill the plot box. Axis labels + dots
  // are HTML so nothing distorts under the non-uniform stretch.
  const xPct = (i: number) => ((i + 0.5) / 7) * 100;
  const yPct = (v: number) => (max > min ? (1 - (v - min) / (max - min)) * 100 : 50);
  const playsPts = dow.map((d, i) => `${xPct(i)},${yPct(d.plays)}`).join(' ');
  const areaPath = `M ${xPct(0)},100 L ${dow.map((d, i) => `${xPct(i)},${yPct(d.plays)}`).join(' L ')} L ${xPct(6)},100 Z`;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-2 px-1 gap-3">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: accent }}>
          <span className="w-0.5 h-3 rounded-full" style={{ backgroundColor: marker }} />
          Plays by day of week
          <RangeBadge label={rangeLabel} accentColor={accent} />
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden flex-1 flex flex-col min-h-0">
        <div className="px-5 pt-4 pb-2 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-2 text-[12px] text-slate-600 min-w-0">
            <Activity className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} />
            {hovered ? (
              <span className="truncate">
                <span className="font-semibold text-slate-900">{hovered.name}</span>:{' '}
                {hovered.plays.toLocaleString('en-US')} plays
              </span>
            ) : (
              'Plays by weekday'
            )}
          </span>
          <Legend accent={accent} />
        </div>

        <div className="px-5 pb-3 flex-1 flex flex-col min-h-0">
          <div className="flex gap-1.5 flex-1 min-h-[120px]">
            {/* y-axis (stretches to the plot height) */}
            <div className="relative w-9 shrink-0">
              {ticks.map((t) => (
                <span
                  key={t}
                  className="absolute right-0 -translate-y-1/2 pr-1 text-[9px] tabular-nums text-slate-400"
                  style={{ top: `${yPct(t)}%` }}
                >
                  {fmtAxis(t)}
                </span>
              ))}
            </div>
            {/* plot — grows to fill the card so it matches the daypart height */}
            <div className="relative flex-1 min-w-0" onMouseLeave={() => setHover(null)}>
              {ticks.map((t) => (
                <div
                  key={t}
                  className="absolute left-0 right-0 border-t"
                  style={{ top: `${yPct(t)}%`, borderColor: t === min ? '#e2e8f0' : '#f1f5f9' }}
                />
              ))}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <path d={areaPath} fill={accent} opacity={0.08} />
                <polyline
                  points={playsPts}
                  fill="none"
                  stroke={accent}
                  strokeWidth="2"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
              {hover != null && (
                <div
                  className="absolute top-0 bottom-0 border-l border-dashed border-slate-300"
                  style={{ left: `${xPct(hover)}%` }}
                />
              )}
              {dow.map((d, i) => (
                <div
                  key={d.name}
                  className="absolute rounded-full -translate-x-1/2 -translate-y-1/2"
                  style={{
                    left: `${xPct(i)}%`,
                    top: `${yPct(d.plays)}%`,
                    width: hover === i ? 9 : 7,
                    height: hover === i ? 9 : 7,
                    backgroundColor: accent,
                  }}
                />
              ))}
              {/* Transparent hover columns so the whole band is hoverable. */}
              <div className="absolute inset-0 flex">
                {dow.map((d, i) => (
                  <div key={d.name} className="flex-1 cursor-pointer" onMouseEnter={() => setHover(i)} />
                ))}
              </div>
            </div>
          </div>
          {/* weekday labels — offset under the plot (y-axis 36px + 6px gap). */}
          <div className="flex mt-2 pl-[42px]">
            {dow.map((d) => (
              <span key={d.name} className="flex-1 text-center text-[10px] text-slate-400">
                {d.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceTrends;
